import { useState } from 'react'
import { cn } from '@/lib/utils'

function initialsFromName(name?: string | null, email?: string | null): string {
  if (name?.trim()) {
    return name
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }
  return (email?.[0] ?? '?').toUpperCase()
}

/**
 * Profile photo with graceful fallback. Uses a real `<img>` (not Base UI’s
 * load-gated AvatarImage) so Google OAuth photos render reliably with
 * `referrerPolicy="no-referrer"`, and failed loads fall back to initials.
 */
export function UserAvatar({
  src,
  name,
  email,
  className,
  fallbackClassName,
  alt = '',
}: {
  src?: string | null
  name?: string | null
  email?: string | null
  className?: string
  fallbackClassName?: string
  alt?: string
}) {
  const [failed, setFailed] = useState(false)
  const imageSrc = src && !failed ? src : null
  const initials = initialsFromName(name, email)

  return (
    <span
      data-slot="avatar"
      className={cn(
        'inline-flex size-8 shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-muted align-middle font-medium text-xs text-foreground',
        className,
      )}
    >
      {imageSrc ? (
        <img
          data-slot="avatar-image"
          src={imageSrc}
          alt={alt}
          referrerPolicy="no-referrer"
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span
          data-slot="avatar-fallback"
          className={cn(
            'flex size-full items-center justify-center rounded-[inherit] bg-muted',
            fallbackClassName,
          )}
        >
          {initials}
        </span>
      )}
    </span>
  )
}
